import { useState } from "react";
import { toast } from "sonner";
import { Captions, Loader2, Wand2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const SUB_STYLES = [
  { id: "tiktok", label: "TikTok 1 mot", css: { fontFamily: "'Luckiest Guy', Impact, sans-serif", color: "#fff", textShadow: "3px 3px 0 #000, -3px -3px 0 #000, 3px -3px 0 #000, -3px 3px 0 #000, 0 6px 0 #000" }, hl: "#ffe03a", emph: "#ff3b5c", upper: true, per: 1, isNew: true },
  { id: "pop", label: "Pop mot à mot", css: { fontFamily: "Anton, Impact, sans-serif", color: "#fff", textShadow: "2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 0 4px 0 #000" }, hl: "#3aff8c", emph: "#ff3b5c", upper: true, per: 3, progressive: true, isNew: true },
  { id: "gaming", label: "Gaming", css: { fontFamily: "Chakra Petch, sans-serif", color: "#fff", textShadow: "0 0 0 #000, 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000" }, hl: "#3ad7ff", upper: true },
  { id: "hormozi", label: "Hormozi", css: { fontFamily: "Anton, Impact, sans-serif", color: "#fff", textShadow: "2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000" }, hl: "#ffe03a", upper: true },
  { id: "mrbeast", label: "MrBeast", css: { fontFamily: "'Luckiest Guy', Impact, sans-serif", color: "#fff", textShadow: "3px 3px 0 #000, -3px -3px 0 #000, 3px -3px 0 #000, -3px 3px 0 #000, 0 5px 0 #000" }, hl: "#ffe03a", upper: true },
  { id: "neon", label: "Néon", css: { fontFamily: "Bangers, Impact, sans-serif", color: "#fff", textShadow: "0 0 6px #ff2bd6, 0 0 12px #ff2bd6" }, hl: "#3ad7ff", upper: true },
  { id: "minimal", label: "Minimal", css: { fontFamily: "Poppins, sans-serif", fontWeight: 800, color: "#fff", textShadow: "0 2px 4px rgba(0,0,0,.8)" }, hl: "#fff", upper: false },
  { id: "ali", label: "Ali Abdaal", css: { fontFamily: "Poppins, sans-serif", fontWeight: 800, color: "#fff", background: "rgba(16,16,16,.7)", padding: "2px 8px", borderRadius: 6 }, hl: "#5bff9c", upper: false },
];
const POS = ["auto", "high", "middle", "low"];

const StyleChip = ({ s, active, onClick }) => (
  <button onClick={onClick} data-testid={`sub-style-${s.id}`}
    className={`relative rounded-xl px-2 py-3 bg-black/50 border transition-colors ${active ? "border-[#3ad7ff] shadow-[0_0_14px_rgba(58,215,255,.35)]" : "border-white/10 hover:border-white/30"}`}>
    {s.isNew && <span className="absolute -top-2 right-1 rounded-full bg-[#ff3b5c] px-1.5 text-[8px] font-bold text-white" data-testid={`sub-style-new-${s.id}`}>NEW</span>}
    <div className="text-[13px] leading-none" style={s.css}>{s.upper ? "TON " : "ton "}<span style={{ color: s.hl }}>{s.upper ? "CLUTCH" : "clutch"}</span></div>
    <div className="text-[9px] text-slate-400 mt-2 font-mono">{s.label}</div>
  </button>
);

const retime = (old, text) => {
  const toks = text.split(/\s+/).filter(Boolean).slice(0, 800);
  if (toks.length === old.length) return old.map((w, i) => ({ ...w, w: toks[i].slice(0, 40) }));
  const s = old[0].s, e = old[old.length - 1].e, step = (e - s) / Math.max(1, toks.length);
  return toks.map((w, i) => ({ w: w.slice(0, 40), s: +(s + i * step).toFixed(3), e: +(s + (i + 1) * step).toFixed(3) }));
};

export const SubtitlesPanel = ({ video, range, cfg, setCfg }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const key = `${range.start.toFixed(1)}-${range.end.toFixed(1)}`;
  const words = cfg.words_key === key ? cfg.words : null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const transcribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/transcribe", { video_id: video.id, start: range.start, end: range.end });
      setCfg((c) => ({ ...c, words: data.words, words_key: key, subs: true }));
      if (!data.words.length) toast.info(t("sub.empty"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="cx-card rounded-2xl p-5" data-testid="subtitles-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white font-semibold"><Captions className="w-4 h-4 text-[#3ad7ff]" />{t("sub.title")}</div>
        <Switch data-testid="subs-switch" checked={cfg.subs} onCheckedChange={(v) => set("subs", v)} />
      </div>
      <p className="text-xs text-slate-500 mt-1">{t("sub.desc")}</p>
      {cfg.subs && (
        <>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {SUB_STYLES.map((s) => <StyleChip key={s.id} s={s} active={cfg.sub_style === s.id} onClick={() => { localStorage.setItem("clipx_sub_style", s.id); set("sub_style", s.id); }} />)}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {POS.map((p) => (
              <button key={p} onClick={() => set("sub_pos", p)} data-testid={`sub-pos-${p}`}
                className={`rounded-full px-3 py-1 text-[11px] ${cfg.sub_pos === p ? "cx-metal cx-metal-active" : "cx-metal"}`}>{t(`sub.pos_${p}`)}</button>
            ))}
            {cfg.sub_pos === "custom" && <span data-testid="sub-pos-custom" className="rounded-full px-3 py-1 text-[11px] cx-metal cx-metal-active">{t("sub.pos_custom")}</span>}
          </div>
          <p className="text-[10px] text-[#3ad7ff]/80 mt-2" data-testid="subs-drag-hint">{t("sub.drag_hint")}</p>
          <div className="flex items-center gap-3 mt-2" data-testid="subs-size-control">
            <span className="text-[11px] text-slate-400 w-16 shrink-0">{t("sub.size")}</span>
            <Slider value={[(cfg.sub_scale ?? 1) * 100]} min={50} max={200} step={5} onValueChange={([v]) => set("sub_scale", v / 100)} />
            <button type="button" onClick={() => set("sub_scale", 1)} data-testid="subs-size-reset" className="text-[11px] font-mono text-white w-12 text-right hover:text-[#3ad7ff]">{Math.round((cfg.sub_scale ?? 1) * 100)}%</button>
          </div>
          {words ? (
            <div className="mt-3">
              <div className="text-[11px] text-slate-400 mb-1">{t("sub.edit")}</div>
              <textarea data-testid="subs-text" defaultValue={words.map((w) => w.w).join(" ")} rows={4}
                onBlur={(e) => words.length && set("words", retime(words, e.target.value))}
                className="w-full rounded-lg bg-black/40 border border-white/10 p-3 text-sm text-slate-200 outline-none focus:border-[#3ad7ff]/50" />
            </div>
          ) : (
            <button onClick={transcribe} disabled={busy} data-testid="subs-transcribe-btn"
              className="cx-ghost rounded-full w-full mt-3 py-2.5 text-xs flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}{t("sub.transcribe")}
            </button>
          )}
          <p className="text-[10px] text-slate-500 mt-2">{t("sub.auto")}</p>
        </>
      )}
    </div>
  );
};
